// src/components/layout/Footer.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Facebook, 
  Instagram,
  Mail,
  Phone,
  MapPin,
  Globe,
  Shield,
  ArrowRight
} from 'lucide-react';
import logo from '../assets/images/logo.png'

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Color theme matching header
  const colors = {
    primary: '#22332D',
    secondary: '#6774AE',
    accent1: '#B7B6AF',
    accent2: '#6AA48A',
    hover: '#70593E',
  };

  // Simple navigation sections
  const footerSections = [
    {
      title: 'Company',
      links: [
        { name: 'About', path: '/about' },
        { name: 'Contact', path: '/contact' },
        {name:'SC Connect',path:'/sc-connect'},
        { name: 'FAQ', path: '/faq' },
      ]
    },
    {
      title: 'Invest',
      links: [
        { name: 'SCCredits', path: '/invest/sccredits' },
        { name: 'Transactions', path: '/invest/transactions' },
        { name: 'Sector', path: '/sector' },
        { name: 'Retirements', path: '/invest/retirements' },
      ]
    },
    {
      title: 'Platform',
      links: [
        { name: 'Sustaincred', path: 'https://www.sustaincred.com/' },
        { name: 'Analysis', path: '/platform/analysis' },
        { name: 'SCMart 360', path: '/platform/SCMart-360' },
        { name: 'Portfolio', path: '/platform/portfolio' },
      ]
    },
    {
      title: 'Cookie Settings',
      links: [
        { name: 'Data Security', path: '/datasecurity/data-security' },
        { name: 'Cookie Policy', path: '/datasecurity/cookie-policy' },
        {name:'Disclaimer ',path:'/datasecurity/disclaimer'},
        { name: 'Information Security', path: '/datasecurity/information-security' },
      ]
    }
  ];

  // Simple legal links
  const legalLinks = [
    { name: 'Privacy', path: '/privacy' },
    { name: 'Terms', path: '/terms' },
    { name: 'Risk', path: '/risk' },
    { name: 'Cookies', path: '/cookies' },
  ];

  // Social links with larger icons
  const socialLinks = [
    // { icon: <Facebook className="w-5 h-5" />, href: '#', label: 'Facebook' },
    // { icon: <Instagram className="w-5 h-5" />, href: '#', label: 'Instagram' },
  ];

  return (
    <footer style={{ backgroundColor: colors.primary }}>
      {/* Simple top border */}
      <div className="h-[2px] w-full" style={{ background: `linear-gradient(90deg, transparent, ${colors.hover}, transparent)` }} />

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
        
        {/* Desktop: 4 Column Layout */}
        <div className="hidden lg:grid lg:grid-cols-5 gap-10">
          {/* Brand Column */}
          <div className="col-span-1">
            <Link to="/" className="inline-block mb-6">
              <div className="flex items-center gap-3">
                <img src={logo} alt="SCMart" className="w-10 h-10 object-contain" />
                <span className="text-2xl font-bold" style={{ color: colors.accent1 }}>
                  <span style={{ color: colors.accent2 }}>SCMart</span>
                </span>
              </div>
            </Link>
            <div className="flex gap-3">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                  style={{ backgroundColor: `${colors.secondary}20`, color: colors.accent1 }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.hover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = `${colors.secondary}20`}
                >
                  {social.icon}
                </a>
              ))}
            </div>
            <p className="text-sm" style={{ color: `${colors.accent1}CC` }}>
              Where Sustainable Investing Meets Transparent Carbon Accounting
            </p>
          </div>

          {/* Navigation Columns */}
          {footerSections.map((section) => (
            <div key={section.title}>
              <h3 className="text-base font-semibold mb-4" style={{ color: colors.accent2 }}>
                {section.title}
              </h3>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      className="text-sm transition-colors inline-block"
                      style={{ color: colors.accent1 }}
                      onMouseEnter={(e) => e.currentTarget.style.color = colors.hover}
                      onMouseLeave={(e) => e.currentTarget.style.color = colors.accent1}
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Mobile: Two-Column Layout */}
        <div className="lg:hidden">
          {/* Brand */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-3">
              <div className="flex items-center justify-center gap-2">
                <img src={logo} alt="SCMart" className="w-10 h-10 object-contain" />
                <span className="text-2xl font-bold" style={{ color: colors.accent1 }}>
                  <span style={{ color: colors.accent2 }}>SCMart</span>
                </span>
              </div>
            </Link>
            <div className="flex justify-center gap-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${colors.secondary}20`, color: colors.accent1 }}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Two-Column Links */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            {footerSections.map((section) => (
              <div key={section.title}>
                <h3 className="text-base font-semibold mb-3" style={{ color: colors.accent2 }}>
                  {section.title}
                </h3>
                <ul className="space-y-2">
                  {section.links.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.path}
                        className="text-sm"
                        style={{ color: colors.accent1 }}
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="my-5 border-t" style={{ borderColor: `${colors.secondary}30` }} />
        {/* Contact Info */}
        <div className="flex flex-col md:flex-row justify-between items-center text-sm gap-4">
          <p className="text-center md:text-left" style={{ color: `${colors.accent1}CC` }}>
            &copy; {new Date().getFullYear()} SustainCred All Rights Reserved
          </p>
          
          {/* <div className="flex flex-wrap justify-center gap-6">
            <span className="flex items-center gap-2" style={{ color: `${colors.accent1}CC` }}>
              <Phone className="w-4 h-4" style={{ color: colors.hover }} />
              +91 73869 25558
            </span>
            <span className="flex items-center gap-2" style={{ color: `${colors.accent1}CC` }}>
              <Mail className="w-4 h-4" style={{ color: colors.hover }} />
              contactus@sustaincred.com
            </span>
            <span className="flex items-center gap-2" style={{ color: `${colors.accent1}CC` }}>
              <Globe className="w-4 h-4" style={{ color: colors.hover }} />
              24/7 Support
            </span>
          </div> */}
          
          <div className="flex space-x-4">
            <Link
              to="/terms-and-conditions"
              className="hover:underline transition-colors duration-200 focus:outline-none focus:underline"
              style={{ color: `${colors.accent1}CC` }}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.hover}
              onMouseLeave={(e) => e.currentTarget.style.color = `${colors.accent1}CC`}
            >
              Terms and Conditions
            </Link>
            <Link
              to="/privacy-policy"
              className="hover:underline transition-colors duration-200 focus:outline-none focus:underline"
              style={{ color: `${colors.accent1}CC` }}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.hover}
              onMouseLeave={(e) => e.currentTarget.style.color = `${colors.accent1}CC`}
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;